import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { AppContext } from '../../../App';
import SelectTrainingSetWithSearch from '../../extractionBatches/create/SelectTrainingSetWithSearch';
import { ITrainingSet } from '../../../types/my-types';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import AddCircleIcon from '@mui/icons-material/AddCircle';

import { useTrainingSetUpdate } from '../../hooks/useTrainingSetUpdate';
import useGetTrainingSet from '../../hooks/useGetTrainingSet';
import ButtonGlynt from '../../common/Buttons/ButtonGlynt';

interface AddToTrainingSetProps {
  documentId: string;
}

function AddToTrainingSet({ documentId }: AddToTrainingSetProps) {
  const { setToast, dataPoolId } = React.useContext(AppContext);

  const [open, setOpen] = React.useState(false);
  const [trainingSet, setTrainingSet] = useState<ITrainingSet>();

  const queryClient = useQueryClient();

  const { mutateAsync: updateTrainingSet, isPending: isLoadingMutation } = useTrainingSetUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSetsByDocument']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Documents added successfully.',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      let message = error.message || 'Sorry, failed to add to training set.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    },
  });

  const { data, isLoading: isLoadingTrainingSet } = useGetTrainingSet(
    trainingSet?.id || '',
    trainingSet !== undefined
  );

  const closeModal = () => {
    setOpen(false);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <ButtonGlynt
          variant="outlined"
          size="xs"
          data-cy="btn-add-to-training-set"
          onClick={() => {
            setOpen(true);
          }}
          sx={{ marginTop: '5px' }}
        >
          <AddCircleIcon sx={{ fontSize: 'small' }} />
          &nbsp;Add
        </ButtonGlynt>
      </div>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        style={{ display: `${open ? '' : 'none'}` }}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') {
            closeModal();
          }
        }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{`Add Document To Existing Training Set`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid container justifyContent="center" alignItems="center" item xs={12}>
              <SelectTrainingSetWithSearch
                setTrainingSet={setTrainingSet}
                disabled={false}
                width="100%"
                trainedOnly={false}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            id="training-set-add-dialog-cancel"
            data-cy="training-set-add-dialog-cancel"
            onClick={() => {
              closeModal();
            }}
            color="primary"
          >
            Cancel
          </Button>

          <Button
            id="button-add-to-training-set-submit"
            color="primary"
            data-cy="button-add-to-training-set-submit"
            onClick={async () => {
              if (!trainingSet || !data) {
                setToast({
                  open: true,
                  variant: 'error',
                  message: 'Unable to get training set data.',
                });
                return;
              }

              await updateTrainingSet({
                trainingSetId: trainingSet.id,
                data: {
                  documents: data.documents.concat(documentId),
                },
              });
            }}
            disabled={isLoadingTrainingSet || isLoadingMutation || !data}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddToTrainingSet;
